import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { CleaningProcedureId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/settings/cleaning-procedures/$procedure_id/",
)({
  component: RouteComponent,
  params: z.object({
    procedure_id: z.coerce
      .number()
      .transform((val) => val as CleaningProcedureId),
  }),
});

function RouteComponent() {
  const { procedure_id } = Route.useParams();
  const utils = trpc.useUtils();

  const { data: procedure, isLoading } =
    trpc.cleaning.procedures.get.useQuery(procedure_id);

  const [localName, setLocalName] = useState("");
  const [localCode, setLocalCode] = useState("");
  const [localDescription, setLocalDescription] = useState<string | null>(null);
  const [localVersion, setLocalVersion] = useState("");
  const [localIsActive, setLocalIsActive] = useState(true);

  useEffect(() => {
    if (procedure) {
      setLocalName(procedure.name);
      setLocalCode(procedure.code);
      setLocalDescription(procedure.description);
      setLocalVersion(procedure.version);
      setLocalIsActive(procedure.is_active);
    }
  }, [procedure]);

  const updateMutation = trpc.cleaning.procedures.update.useMutation({
    onMutate: async (newData) => {
      await utils.cleaning.procedures.get.cancel(procedure_id);
      const previous = utils.cleaning.procedures.get.getData(procedure_id);
      utils.cleaning.procedures.get.setData(procedure_id, (old) =>
        old ? { ...old, ...newData } : undefined,
      );
      return { previous };
    },
    onError: (err, _newData, context) => {
      toast.error(`Failed to update: ${err.message}`);
      if (context?.previous) {
        utils.cleaning.procedures.get.setData(procedure_id, context.previous);
      }
    },
    onSettled: () => {
      utils.cleaning.procedures.get.invalidate(procedure_id);
      utils.cleaning.procedures.list.invalidate();
    },
    onSuccess: () => {
      toast.success("Updated");
    },
  });

  const handleSave = () => {
    if (!localName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!localCode.trim()) {
      toast.error("Code is required");
      return;
    }
    updateMutation.mutate({
      id: procedure_id,
      name: localName,
      code: localCode,
      description: localDescription,
      version: localVersion,
      isActive: localIsActive,
    });
  };

  const handleActiveToggle = (checked: boolean) => {
    setLocalIsActive(checked);
    updateMutation.mutate({
      id: procedure_id,
      isActive: checked,
    });
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!procedure) {
    return <div className="p-4 text-destructive">Not found</div>;
  }

  return (
    <div className="p-4 space-y-4 max-w-2xl">
      <div className="text-sm font-medium">Overview</div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-1">
          <Label htmlFor="name" className="text-xs">
            Name
          </Label>
          <Input
            id="name"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleSave}
            disabled={updateMutation.isPending}
            className="h-8"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="code" className="text-xs">
            Code
          </Label>
          <Input
            id="code"
            value={localCode}
            onChange={(e) => setLocalCode(e.target.value)}
            onBlur={handleSave}
            disabled={updateMutation.isPending}
            className="h-8 font-mono text-xs"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label htmlFor="version" className="text-xs">
            Version
          </Label>
          <Input
            id="version"
            value={localVersion}
            onChange={(e) => setLocalVersion(e.target.value)}
            onBlur={handleSave}
            disabled={updateMutation.isPending}
            className="h-8"
          />
        </div>
        <div className="col-span-2 flex items-end pb-1">
          <div className="flex items-center gap-2">
            <Switch
              id="is_active"
              checked={localIsActive}
              onCheckedChange={handleActiveToggle}
              disabled={updateMutation.isPending}
            />
            <Label htmlFor="is_active" className="text-sm">
              Active
            </Label>
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="description" className="text-xs">
          Description
        </Label>
        <Textarea
          id="description"
          value={localDescription ?? ""}
          onChange={(e) => setLocalDescription(e.target.value || null)}
          onBlur={handleSave}
          disabled={updateMutation.isPending}
          rows={3}
          className="text-sm"
        />
      </div>
    </div>
  );
}
